import { summarizeReadingStreaks, type ReadingStreakDay, type ReadingStreakSummary } from "./reading-streaks";

const DAY_MS = 24 * 60 * 60 * 1000;
const HEATMAP_WEEKS = 12;
const WEEKDAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;

export interface ReadingHabitHeatmapDay {
  day: string;
  totalMs: number;
  intensity: 0 | 1 | 2 | 3 | 4;
  future: boolean;
}

export interface ReadingHabitWeekday {
  label: string;
  shortLabel: string;
  activeDays: number;
  totalMs: number;
}

export interface ReadingHabitSummary {
  streaks: ReadingStreakSummary;
  heatmapDays: ReadingHabitHeatmapDay[];
  typicalDayMs: number;
  recentFourWeeksMs: number;
  previousFourWeeksMs: number;
  recentChangePercent: number | null;
  activeWeeks: number;
  favoriteWeekday: ReadingHabitWeekday | null;
}

function dayOrdinal(day: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(day);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const date = Number(match[3]);
  const timestamp = Date.UTC(year, month, date);
  const parsed = new Date(timestamp);
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month || parsed.getUTCDate() !== date) return null;
  return Math.floor(timestamp / DAY_MS);
}

function dayFromOrdinal(ordinal: number): string {
  return new Date(ordinal * DAY_MS).toISOString().slice(0, 10);
}

function mondayBasedWeekday(ordinal: number): number {
  return (new Date(ordinal * DAY_MS).getUTCDay() + 6) % 7;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[middle] ?? 0;
  return Math.round(((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2);
}

function totalBetween(totals: Map<number, number>, from: number, to: number): number {
  let totalMs = 0;
  for (let ordinal = from; ordinal <= to; ordinal += 1) totalMs += totals.get(ordinal) ?? 0;
  return totalMs;
}

function readingIntensity(totalMs: number, typicalDayMs: number): 0 | 1 | 2 | 3 | 4 {
  if (totalMs <= 0) return 0;
  if (typicalDayMs <= 0) return 1;
  const ratio = totalMs / typicalDayMs;
  if (ratio <= 0.5) return 1;
  if (ratio <= 1) return 2;
  if (ratio <= 1.5) return 3;
  return 4;
}

export function summarizeReadingHabits(days: ReadingStreakDay[], asOfDay: string): ReadingHabitSummary {
  const totals = new Map<number, number>();
  for (const entry of days) {
    const ordinal = dayOrdinal(entry.day);
    if (ordinal === null || entry.totalMs <= 0) continue;
    totals.set(ordinal, (totals.get(ordinal) ?? 0) + entry.totalMs);
  }

  const streaks = summarizeReadingStreaks(days, asOfDay);
  const asOfOrdinal = dayOrdinal(asOfDay);
  if (asOfOrdinal === null) {
    return {
      streaks,
      heatmapDays: [],
      typicalDayMs: 0,
      recentFourWeeksMs: 0,
      previousFourWeeksMs: 0,
      recentChangePercent: null,
      activeWeeks: 0,
      favoriteWeekday: null
    };
  }

  const currentWeekStart = asOfOrdinal - mondayBasedWeekday(asOfOrdinal);
  const heatmapStart = currentWeekStart - (HEATMAP_WEEKS - 1) * 7;
  const heatmapEnd = heatmapStart + HEATMAP_WEEKS * 7 - 1;
  const activeHeatmapTotals: number[] = [];
  for (let ordinal = heatmapStart; ordinal <= asOfOrdinal; ordinal += 1) {
    const totalMs = totals.get(ordinal) ?? 0;
    if (totalMs > 0) activeHeatmapTotals.push(totalMs);
  }
  const typicalDayMs = median(activeHeatmapTotals);
  const heatmapDays: ReadingHabitHeatmapDay[] = [];
  const activeWeekIndexes = new Set<number>();
  const weekdayTotals = WEEKDAY_LABELS.map((label) => ({
    label,
    shortLabel: label.slice(0, 3),
    activeDays: 0,
    totalMs: 0
  }));

  for (let ordinal = heatmapStart; ordinal <= heatmapEnd; ordinal += 1) {
    const future = ordinal > asOfOrdinal;
    const totalMs = future ? 0 : totals.get(ordinal) ?? 0;
    const weekday = mondayBasedWeekday(ordinal);
    if (totalMs > 0) {
      activeWeekIndexes.add(Math.floor((ordinal - heatmapStart) / 7));
      const weekdayTotal = weekdayTotals[weekday];
      if (weekdayTotal) {
        weekdayTotal.activeDays += 1;
        weekdayTotal.totalMs += totalMs;
      }
    }
    heatmapDays.push({
      day: dayFromOrdinal(ordinal),
      totalMs,
      intensity: readingIntensity(totalMs, typicalDayMs),
      future
    });
  }

  const favoriteWeekday = weekdayTotals
    .filter((weekday) => weekday.activeDays > 0)
    .sort((left, right) => right.activeDays - left.activeDays || right.totalMs - left.totalMs)[0] ?? null;
  const recentFourWeeksMs = totalBetween(totals, asOfOrdinal - 27, asOfOrdinal);
  const previousFourWeeksMs = totalBetween(totals, asOfOrdinal - 55, asOfOrdinal - 28);
  const recentChangePercent = previousFourWeeksMs > 0
    ? Math.round(((recentFourWeeksMs - previousFourWeeksMs) / previousFourWeeksMs) * 100)
    : null;

  return {
    streaks,
    heatmapDays,
    typicalDayMs,
    recentFourWeeksMs,
    previousFourWeeksMs,
    recentChangePercent,
    activeWeeks: activeWeekIndexes.size,
    favoriteWeekday
  };
}
